import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  MapPin,
  FileDown,
  ChevronDown,
  ChevronUp,
  Trash2
} from "lucide-react";
import { OrderShipment, useAdvanceShipmentStatus, useDeleteShipment } from "@/hooks/use-order-shipments";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog";

interface ShipmentCardProps {
  shipment: OrderShipment;
  onGenerateDeliveryNote?: (shipment: OrderShipment) => void;
  isGeneratingDeliveryNote?: boolean;
}

export const ShipmentCard = ({ shipment, onGenerateDeliveryNote, isGeneratingDeliveryNote }: ShipmentCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  
  const advanceStatus = useAdvanceShipmentStatus();
  const deleteShipment = useDeleteShipment();

  // Look up friendly SSL serial numbers for the allocated devices
  const { data: serialByDeviceId = {} } = useQuery({
    queryKey: ['device-serials', shipment.allocated_device_ids],
    queryFn: async () => {
      if (!shipment.allocated_device_ids || shipment.allocated_device_ids.length === 0) return {};
      const { data } = await supabase
        .from('device_details')
        .select('device_id, device_serial')
        .in('device_id', shipment.allocated_device_ids);
      return Object.fromEntries((data || []).map((d) => [d.device_id, d.device_serial]));
    },
    enabled: shipment.allocated_device_ids.length > 0,
  });

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      'pending': { label: 'Pending', className: 'bg-gray-100 text-gray-800' },
      'ready_to_ship': { label: 'Ready to Ship', className: 'bg-blue-100 text-blue-800' },
      'shipped': { label: 'Shipped', className: 'bg-purple-100 text-purple-800' },
      'delivered': { label: 'Delivered', className: 'bg-green-100 text-green-800' },
      'confirmed': { label: 'Confirmed', className: 'bg-emerald-100 text-emerald-800' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-gray-500" />;
      case 'ready_to_ship':
        return <Package className="h-5 w-5 text-blue-500" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-purple-500" />;
      case 'delivered':
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const handleAdvanceStatus = async (action: 'ready_to_ship' | 'ship' | 'deliver' | 'confirm') => {
    await advanceStatus.mutateAsync({
      shipmentId: shipment.id,
      action,
      metadata: action === 'ship' ? { trackingNumber, carrier } : undefined
    });
    
    if (action === 'ship') {
      setTrackingNumber('');
      setCarrier('');
    }
  };

  const canDelete = shipment.status === 'pending';

  return (
    <Card className="border-l-4" style={{ borderLeftColor: shipment.status === 'delivered' || shipment.status === 'confirmed' ? '#22c55e' : '#e5e7eb' }}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(shipment.status)}
            <CardTitle className="text-base">
              Shipment #{shipment.shipment_number}
            </CardTitle>
            {getStatusBadge(shipment.status)}
          </div>
          <div className="flex items-center gap-2">
            {canDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Shipment</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete Shipment #{shipment.shipment_number}? 
                      This action cannot be undone. All allocated devices will be returned to the pool.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteShipment.mutate(shipment.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {/* Summary row */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Package className="h-3 w-3" />
            {shipment.allocated_device_ids.length} device{shipment.allocated_device_ids.length !== 1 ? 's' : ''}
          </span>
          {shipment.allocated_credits > 0 && (
            <span>{shipment.allocated_credits.toLocaleString()} credits</span>
          )}
          {shipment.tracking_number && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {shipment.tracking_number}
            </span>
          )}
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="mt-4 space-y-4 pt-4 border-t">
            {/* Devices list */}
            {shipment.allocated_device_ids.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Allocated Devices</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {shipment.allocated_device_ids.map((deviceId, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {serialByDeviceId[deviceId] || deviceId}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {shipment.shipped_at && (
                <div>
                  <Label className="text-xs text-muted-foreground">Shipped At</Label>
                  <p>{new Date(shipment.shipped_at).toLocaleString()}</p>
                </div>
              )}
              {shipment.delivered_at && (
                <div>
                  <Label className="text-xs text-muted-foreground">Delivered At</Label>
                  <p>{new Date(shipment.delivered_at).toLocaleString()}</p>
                </div>
              )}
            </div>

            {/* Notes */}
            {shipment.notes && (
              <div>
                <Label className="text-xs text-muted-foreground">Notes</Label>
                <p className="text-sm">{shipment.notes}</p>
              </div>
            )}

            {/* Action buttons based on status */}
            <div className="flex gap-2 pt-2">
              {shipment.status === 'pending' && (
                <Button
                  size="sm"
                  onClick={() => handleAdvanceStatus('ready_to_ship')}
                  disabled={advanceStatus.isPending}
                  style={{ backgroundColor: '#DFEF97', color: 'black', border: '1px solid #DFEF97' }}
                  className="hover:opacity-90"
                >
                  Mark Ready to Ship
                </Button>
              )}

              {shipment.status === 'ready_to_ship' && (
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Tracking number"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Carrier (optional)"
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      className="w-32"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAdvanceStatus('ship')}
                    disabled={advanceStatus.isPending || !trackingNumber.trim()}
                    style={{ backgroundColor: '#DFEF97', color: 'black', border: '1px solid #DFEF97' }}
                    className="hover:opacity-90 w-fit"
                  >
                    Mark Shipped
                  </Button>
                </div>
              )}

              {shipment.status === 'shipped' && (
                <Button
                  size="sm"
                  onClick={() => handleAdvanceStatus('deliver')}
                  disabled={advanceStatus.isPending}
                  style={{ backgroundColor: '#DFEF97', color: 'black', border: '1px solid #DFEF97' }}
                  className="hover:opacity-90"
                >
                  Mark Delivered
                </Button>
              )}

              {shipment.status === 'delivered' && (
                <Button
                  size="sm"
                  onClick={() => handleAdvanceStatus('confirm')}
                  disabled={advanceStatus.isPending}
                  variant="outline"
                >
                  Confirm Receipt
                </Button>
              )}

              {(shipment.status === 'ready_to_ship' || shipment.status === 'shipped' || shipment.status === 'delivered') && onGenerateDeliveryNote && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onGenerateDeliveryNote(shipment)}
                  disabled={isGeneratingDeliveryNote}
                  className="flex items-center gap-1"
                >
                  <FileDown className="h-3 w-3" />
                  {isGeneratingDeliveryNote ? 'Generating...' : 'Delivery Note'}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
